import React, { FC } from 'react';

import { EvInfoTooltip, EvSwitch, useDebounce } from '@evinced-private/ui-common';

import {
	DASHBOARD_FILTERS_ID,
	defaultFilterValues
} from '../../../pages/dashboard/helpers/DashboardFiltersHelper';
import { PROPERTIES_PAGE_FILTERS_ID } from '../../../pages/properties/helpers/PropertiesPageFiltersHelper';
import {
	MY_COLLECTION_NAME,
	useCollections
} from '../../../providers/collections/CollectionsProvider';
import { useCommonFiltersStore } from '../../../providers/commonFiltersProvider/CommonFiltersProvider';
import { useTablesStore } from '../../../providers/tablesProvider/TablesProvider';

const screenId = 'properties-table';

interface ICollectionsSwitch {
	showOnlyPropertiesInMyCollection: boolean;
	setShowOnlyPropertiesInMyCollection: (value: boolean) => void;
	isDisabled?: boolean;
}

const CollectionsSwitch: FC<ICollectionsSwitch> = ({
	setShowOnlyPropertiesInMyCollection,
	showOnlyPropertiesInMyCollection,
	isDisabled
}) => {
	const tablesStore = useTablesStore();
	const commonFiltersStore = useCommonFiltersStore();
	const { myCollection } = useCollections();
	const debouncedShowOnlyPropertiesInMyCollection = useDebounce(
		showOnlyPropertiesInMyCollection,
		300
	);
	const collectionsSwitchLabel = `Filter by "${MY_COLLECTION_NAME}"`;

	const appliedFilters = commonFiltersStore.getFilterValue(DASHBOARD_FILTERS_ID);

	const updateShowCollectionsTableToggle = (value: boolean): void => {
		const { sizePerPage } = tablesStore.getPagination(screenId);
		const collections = value ? [{ label: myCollection.name, value: myCollection.id }] : [];

		tablesStore.updatePagination(screenId, { page: 1, sizePerPage });
		commonFiltersStore.updateSelectedFilters(DASHBOARD_FILTERS_ID, {
			collections,
			liftPeriods: appliedFilters?.liftPeriods || defaultFilterValues.liftPeriods
		});
		commonFiltersStore.updateSelectedFilters(PROPERTIES_PAGE_FILTERS_ID, { collections });

		setShowOnlyPropertiesInMyCollection(value);
	};

	return (
		<>
			<span>{collectionsSwitchLabel}</span>
			<EvInfoTooltip title={collectionsSwitchLabel} tooltipPlace="top">
				Collect and track properties. To add a property, click “Add
				<br />
				to {MY_COLLECTION_NAME}” under the actions menu.
				<br />
				Active properties, those scanned within the last 90 days.
			</EvInfoTooltip>
			<EvSwitch
				disabled={isDisabled}
				onChange={updateShowCollectionsTableToggle}
				checked={debouncedShowOnlyPropertiesInMyCollection}
				ariaLabel={collectionsSwitchLabel}
			/>
		</>
	);
};

export { CollectionsSwitch };
